import { QueryObserverOptions } from "@tanstack/react-query"

import { PageRequest } from "@shared/dtos/requests/pageRequest.dto"
import {
  CheckHisConnectionRequest,
  CreateCustomOutcomeRequest,
  EditPracticeRequest,
  UpdateCareGroupRequest,
} from "@shared/dtos/requests/practiceRequests.dto"
import { DefaultResponse, PracticeResponse } from "@shared/dtos/responses/commonResponses.dto"
import { PlatoQuestionnaireInfoResponse } from "@shared/dtos/responses/platoResponses.dto"
import {
  AutoMedicConnectionResponse,
  CheckConnectionResponse,
  PracticeWithConfigResponse,
} from "@shared/dtos/responses/practiceResponses.dto"
import { TriageOutcomeOverview } from "@shared/dtos/responses/triageOutcomeResponses.dto"
import { Duration } from "@shared/types/duration"
import { FileRequest } from "@shared/zod/schemas/requests/file"
import { PostalAddressRequest, TestVecozoConnectionRequest } from "@shared/zod/schemas/requests/practiceRequests"
import { TestVecozoConnectionResponse } from "@shared/zod/schemas/responses/practice"

import { axiosAuth } from "./generic/AxiosInterceptor/AxiosInterceptor"

// Sets defaults used in `buildApiFromServices`
export const queryOptions: Partial<QueryObserverOptions> = {
  staleTime: 5 * 60 * 1000, // 5 minutes
}

export const getAll = (): Promise<PracticeResponse[]> => {
  return axiosAuth({
    url: "/practices",
    method: "get",
  }).then((resp) => {
    return resp.data
  })
}

export const get = (id: string): Promise<PracticeWithConfigResponse> => {
  return axiosAuth({
    url: `/practices/${id}`,
    method: "get",
  }).then((resp) => resp.data)
}

export const edit = (id: string, request: EditPracticeRequest): Promise<void> => {
  return axiosAuth({
    url: `/practices/${id}`,
    method: "patch",
    data: request,

    // Saving vecozoIon or registrationAgb triggers a Vecozo ION permission
    // probe per AGB code, far longer than the default REQUEST_TIMEOUT allows.
    timeout: Duration.from("35 seconds").milliseconds,
  }).then(() => undefined)
}

/** Set (create or replace) a practice's custom address override. */
export const setAddress = (id: string, request: PostalAddressRequest): Promise<void> => {
  return axiosAuth({
    url: `/practices/${id}/address`,
    method: "patch",
    data: request,
  }).then(() => undefined)
}

/** Clear a practice's custom address override (fall back to the AGB address). */
export const clearAddress = (id: string): Promise<void> => {
  return axiosAuth({
    url: `/practices/${id}/address`,
    method: "delete",
  }).then(() => undefined)
}

export const checkHisConnection = async (
  id: string,
  request: CheckHisConnectionRequest
): Promise<CheckConnectionResponse> => {
  const resp = await axiosAuth({
    url: `/practices/${id}/checkhisconnection`,
    method: "post",
    data: request,
  })
  return resp.data
}

/** Probes the Vecozo COV/ION permissions for the given (unsaved) registration settings. */
export const testVecozoConnection = async (
  id: string,
  request: TestVecozoConnectionRequest
): Promise<TestVecozoConnectionResponse> => {
  const resp = await axiosAuth({
    url: `/practices/${id}/vecozo/test`,
    method: "post",
    data: request,

    // One ION permission probe per AGB code, like saving the settings.
    timeout: Duration.from("35 seconds").milliseconds,
  })
  return resp.data
}

export const createAutomedicConnection = (id: string): Promise<AutoMedicConnectionResponse> => {
  return axiosAuth({
    url: `/practices/${id}/automedicconnection`,
    method: "post",
  }).then((resp) => resp.data)
}

export const deleteAutomedicConnection = (id: string): Promise<DefaultResponse> => {
  return axiosAuth({
    url: `/practices/${id}/automedicconnection`,
    method: "delete",
  }).then((resp) => resp.data)
}

export const confirmAccessRights = (id: string): Promise<DefaultResponse> => {
  return axiosAuth({
    url: `/practices/${id}/authorizations/confirm`,
    method: "patch",
  })
}

/** Links (or removes) care group to a practice */
export const updateCareGroup = (practiceId: string, data: UpdateCareGroupRequest): Promise<DefaultResponse> => {
  return axiosAuth({
    url: `/admin/practices/${practiceId}/update-care-group`,
    method: "patch",
    data,
  })
}

/** Adds new logo */
export const addLogo = async (practiceId: string, data: FileRequest): Promise<void> => {
  if (!practiceId) {
    return undefined
  }

  return await axiosAuth({
    url: `/practices/${practiceId}/logo`,
    method: "post",
    data,
  })
}

/** Removes the logo */
export const deleteLogo = async (practiceId: string): Promise<void> => {
  if (!practiceId) {
    return undefined
  }

  return await axiosAuth({
    url: `/practices/${practiceId}/logo`,
    method: "delete",
  })
}

/** List of Plato outcomes to display in an overview */
export const getTriageOutcomes = async (practiceId: string, query?: PageRequest): Promise<TriageOutcomeOverview> => {
  const resp = await axiosAuth({
    url: `/practices/${practiceId}/triage-outcomes`,
    method: "get",
    params: query,
  })
  return resp.data
}

/** Adds custom Plato outcome (override for the role/type) */
export const createCustomOutcome = async (practiceId: string, req: CreateCustomOutcomeRequest): Promise<void> => {
  const resp = await axiosAuth({
    url: `/practices/${practiceId}/triage-outcomes`,
    method: "post",
    data: req,
  })
  return resp.data
}

/** Resets the Plato outcome to original state*/
export const resetOutcome = async (practiceId: string, outcomeId: string): Promise<void> => {
  const resp = await axiosAuth({
    url: `/practices/${practiceId}/triage-outcomes/${outcomeId}`,
    method: "delete",
  })
  return resp.data
}

/** Get list of active questionnaires for filtering */
export const getQuestionnaires = async (practiceId: string): Promise<PlatoQuestionnaireInfoResponse[]> => {
  const resp = await axiosAuth({
    url: `/practices/${practiceId}/questionnaires`,
    method: "get",
  })
  return resp.data
}
